import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ApiError,
  listSessions,
  logout,
  logoutAll,
  revokeSession,
  stepUpPasskey,
  type AuthSessionRow,
} from '../lib/api'
import { useSession } from '../lib/useSession'
import { StepUpModal } from '../components/StepUpModal'

export function SessionsPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useSession()
  const [sessions, setSessions] = useState<AuthSessionRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [needStepUp, setNeedStepUp] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await listSessions()
      setSessions(res.sessions)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.sessionsFailed'))
    }
  }, [t])

  useEffect(() => {
    if (isAuthenticated) void load()
  }, [isAuthenticated, load])

  if (!isAuthenticated) return <Navigate to="/passkey" replace />

  async function onRevoke(id: string) {
    setBusy(true)
    setError(null)
    try {
      await revokeSession(id)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.sessionsFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function onLogout() {
    await logout()
    window.location.assign('/welcome')
  }

  async function onLogoutAll() {
    setBusy(true)
    setError(null)
    try {
      await logoutAll()
      window.location.assign('/welcome')
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setNeedStepUp(true)
      } else {
        setError(err instanceof ApiError ? err.message : t('auth.logoutAllFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="welcome card wide">
      <h1>{t('auth.sessionsTitle')}</h1>
      <p className="lede">{t('auth.sessionsLede')}</p>
      {error ? (
        <p className="warn" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="session-list">
        {sessions.map((s) => (
          <li key={s.id} className="session-row">
            <div>
              <strong>{s.device_label || s.user_agent || s.id.slice(0, 8)}</strong>
              {s.current ? <span className="pill">{t('auth.currentSession')}</span> : null}
              <p className="muted">
                AMR: {(s.amr || []).join(', ') || '—'} · {s.ip_prefix || 'ip?'}
              </p>
            </div>
            {!s.current ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void onRevoke(s.id)}>
                {t('auth.revoke')}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="welcome-actions">
        <button type="button" className="btn" disabled={busy} onClick={() => void onLogout()}>
          {t('auth.logout')}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void onLogoutAll()}>
          {t('auth.logoutAll')}
        </button>
        <Link className="btn btn-ghost" to="/security/passkey">
          {t('auth.registerPasskey')}
        </Link>
        <Link className="btn btn-ghost" to="/accounts">
          {t('auth.accounts')}
        </Link>
      </div>

      {needStepUp ? (
        <StepUpModal
          onCancel={() => setNeedStepUp(false)}
          onVerified={() => {
            setNeedStepUp(false)
            void onLogoutAll()
          }}
          runStepUp={stepUpPasskey}
        />
      ) : null}
    </section>
  )
}
